/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { BehaviorSubject, Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ApplicationError, CoreErrorKey, AlertService, AlertKinds } from '@typescript-kit/core';
import { User, CredentialsData, UserData } from '@typescript-kit/authentication';
import { HttpService, PersistenceService } from '@angular-kit/core';

import { AuthenticationPersistenceKey } from '../key/authentication-persistence.key';


@Injectable()
export class AuthenticationService {
  static readonly REFRESH_AUTHENTICATION_TIMEOUT = 60 * 1000; // 60 seconds (used for activity signal)

  private _authenticationToken: string = null;
  private _authenticationTime: number = 0;
  private _authenticationTimeout: number = 0;

  private currentUserSubject: BehaviorSubject<User>;

  private expireAuthenticationSchedule: any;
  private refreshAuthenticationSchedule: any;

  constructor(
    private router: Router,
    private alertService: AlertService,
    private persistenceService: PersistenceService,
    private httpService: HttpService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(undefined);
    this._authenticationTimeout = persistenceService.getValue(
      AuthenticationPersistenceKey.AUTHENTICATION_TIMEOUT, 0, true
    );
    const initialTokenMatch = /[?&]authorization=([^&]*)/.exec(window.location.search);
    if (initialTokenMatch) {
      this.authenticationToken = decodeURIComponent(initialTokenMatch[1]) || null;
      return;
    }
    this._authenticationToken = persistenceService.getValue(
      AuthenticationPersistenceKey.AUTHENTICATION_TOKEN, null, true
    );
    this._authenticationTime = persistenceService.getValue(
      AuthenticationPersistenceKey.AUTHENTICATION_TIME, 0, true
    );
    if (this.authenticationToken) {
      this.scheduleExpireAuthentication();
    }
  }

  get currentUser(): User {
    return this.currentUserSubject.getValue();
  }

  get currentUserChanged(): Observable<User> {
    return this.currentUserSubject.asObservable();
  }

  get authenticationToken(): string {
    return this._authenticationToken;
  }

  set authenticationToken(value: string) {
    if (this.authenticationToken === value) {
      return;
    }
    this._authenticationToken = value;
    this.persistenceService.setValue(AuthenticationPersistenceKey.AUTHENTICATION_TOKEN, value, true);
    this.clearRefreshAuthenticationSchedule();
    if (value) {
      this.authenticationTime = Date.now();
    } else {
      this.authenticationTime = null;
      this.setCurrentUser(null);
    }
  }

  get authenticationTime(): number {
    return this._authenticationTime;
  }

  set authenticationTime(value: number) {
    if (this.authenticationTime === value) {
      return;
    }
    this._authenticationTime = value;
    this.persistenceService.setValue(AuthenticationPersistenceKey.AUTHENTICATION_TIME, value, true);
    this.scheduleExpireAuthentication();
  }

  get authenticationTimeout(): number {
    return this._authenticationTimeout;
  }

  set authenticationTimeout(value: number) {
    if (this.authenticationTimeout === value) {
      return;
    }
    this._authenticationTimeout = value;
    this.persistenceService.setValue(AuthenticationPersistenceKey.AUTHENTICATION_TIMEOUT, value, true);
    this.scheduleExpireAuthentication();
  }

  get authenticationAge(): number {
    return Date.now() - this.authenticationTime;
  }

  async resetPassword(email: string): Promise<void> {
    return this.httpService.post(`api/register/reset-password`, { email });
  }

  async login(credentials: CredentialsData): Promise<User> {
    return this.httpService.post(`api/authentication/login`, credentials)
      .then((data: { user: UserData, timeout: number }) => {
        this.authenticationTimeout = data.timeout || null;
        const user = new User(data.user);
        this.setCurrentUser(user);
        return user;
      })
      .catch((error: ApplicationError) => {
        this.setCurrentUser(null);
        // TODO return this.errorService.handleError(error);
        throw error;
      });
  }

  async logout(): Promise<User> {
    return this.httpService.post(`api/authentication/logout`)
      .then((userData: UserData) => {
        this.setCurrentUser(null);
        return new User(userData);
      })
      .catch((error: ApplicationError) => {
        this.setCurrentUser(null);
        // TODO return this.errorService.handleError(error);
        throw error;
      });
  }

  async refreshAuthentication() {
    return this.httpService.get(`api/authentication/refresh`)
      .catch((error: ApplicationError) => {
        // TODO return this.errorService.handleError(error);
        throw error;
      });
  }

  async resolveCurrentUser(): Promise<User> {
    return this.currentUser !== undefined
      ? Promise.resolve(this.currentUser)
      : this.refreshCurrentUser();
  }

  async refreshCurrentUser(): Promise<User> {
    return this.loadCurrentUser()
      .then((user: User) => {
        this.setCurrentUser(user);
        return user;
      })
      .catch(() => {
        this.setCurrentUser(null);
        return null;
      });
  }

  async loadCurrentUser(): Promise<User> {
    if (!this.authenticationToken) {
      return Promise.resolve(null);
    }
    return this.httpService.get('api/authentication/current')
      .then((userData: UserData) => {
        return userData ? new User(userData) : null;
      })
      .catch((error: ApplicationError) => {
        // TODO return this.errorService.handleError(error);
        throw error;
      });
  }

  signalActivity() {
    this.scheduleRefreshAuthentication();
  }

  private setCurrentUser(user: User) {
    if (this.currentUser === user) {
      return;
    }
    const userId = user && user.id;
    this.persistenceService.scope = userId;
    if (user === null) {
      this.authenticationToken = null;
    }
    this.currentUserSubject.next(user);
  }

  private scheduleExpireAuthentication() {
    if (this.expireAuthenticationSchedule) {
      // RE-SCHEDULE EXPIRE AUTHENTICATION
      clearTimeout(this.expireAuthenticationSchedule);
    }
    if (this.authenticationTime && this.authenticationTimeout) {
      this.expireAuthenticationSchedule = setTimeout(() => {
        this.expireAuthenticationSchedule = null;
        this.expireAuthentication();
      }, Math.max(this.authenticationTime + this.authenticationTimeout - Date.now() - 5000, 0));
    } else {
      this.expireAuthenticationSchedule = null;
    }
  }

  private expireAuthentication() {
    this.logout()
      .catch((error: ApplicationError) => {
        // DO NOTHING
      })
      .then(() => this.router.navigate(['/login']))
      .then(() => {
        this.alertService.alert({
          kind: AlertKinds.WARNING,
          message: CoreErrorKey.AUTHENTICATION_EXPIRED,
          timeout: 0
        });
      });
  }

  private scheduleRefreshAuthentication(timeout?: number) {
    if (this.refreshAuthenticationSchedule) {
      // DO NOT RE-SCHEDULE REFRESH AUTHENTICATION, IF ALREADY SCHEDULED
      return;
    }
    if (timeout === undefined) {
      Math.max(this.authenticationTime + AuthenticationService.REFRESH_AUTHENTICATION_TIMEOUT - Date.now(), 5000);
    }
    this.refreshAuthenticationSchedule = setTimeout(() => {
      this.refreshAuthenticationSchedule = null;
      this.refreshAuthentication()
        .catch((error: ApplicationError) => {
          // RE-SCHEDULE REFRESH AUTHENTICATION
          this.scheduleRefreshAuthentication(5000);
        });
    }, timeout);
  }

  private clearRefreshAuthenticationSchedule() {
    if (this.refreshAuthenticationSchedule) {
      clearTimeout(this.refreshAuthenticationSchedule);
      this.refreshAuthenticationSchedule = null;
    }
  }

}
